import {
  Component,
  input,
  output,
  computed,
  OnInit,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { CentroTrabajo } from 'src/app/domain/model/beneficiario/beneficiario';
import { BeneficiarioRepository } from 'src/app/domain/service/beneficiario/beneficiario-repository';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import {
  HttpSucursalRepository
} from 'src/app/infrastructure/adapters/sin-oc/http-sucursal.repository';
import { busquedaCentroTrabajoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseBusquedaPaginadaDialog } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

export interface FiltroCentroTrabajo {
  codigo?: string;
  nombre?: string;
  nmconsecutivoBeneficiario?: number;
  start?: number;
  length?: number;
}

/**
 * Origen de datos del buscador de centros de trabajo.
 *
 * BENEFICIARIO:    TPSC_PERSON_CENTRO_TRABA filtrado por nmconsecutivoBeneficiario
 * SUCURSAL_SIN_OC: T094_SUCURSALES filtrado por NPOLIZA (Solo Sin Orientación de Servicio)
 */
export enum BuscadorCentroTrabajoOrigen {
  BENEFICIARIO    = 'BENEFICIARIO',
  SUCURSAL_SIN_OC = 'SUCURSAL_SIN_OC',
}

export interface CentroTrabajoSeleccionado {
  nmconsecutivo: number;
  cdcentroTrabajo: string;
  dscentroTrabajo: string;
  /** Descripción completa: código - nombre - nombre comercial (del backend) */
  descripcionCompleta: string;
}

@Component({
  selector: 'app-busqueda-centro-trabajo-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    TooltipModule,
    DropdownModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="titulo() || 'Filtros de Búsqueda'"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '44rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      @if (!tieneBeneficiario()) {
        <div
          class="p-3 mb-3 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded text-sm"
        >
          <i class="pi pi-info-circle mr-2"></i>
          Sin cliente seleccionado se mostrarán todos los centros de trabajo
          activos.
        </div>
      }
      <div
        class="border dark:border-gray-600 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm text-gray-600 mb-1">Código</label>
            <input
              pInputText
              [(ngModel)]="filtro.codigo"
              (input)="sanitizarCampo('codigo')"
              class="w-full"
              maxlength="20"
              (keyup.enter)="buscar()"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1"
              >Centro de Trabajo</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.nombre"
              (input)="sanitizarCampo('nombre')"
              class="w-full"
              maxlength="100"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>
        <div
          class="flex flex-wrap gap-2 sm:gap-3 mt-4 justify-end sm:justify-start w-full"
        >
          <p-button
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            [outlined]="true"
            size="small"
            styleClass="min-h-[44px]"
            (onClick)="limpiarFiltros()"
          />
          <p-button
            label="Buscar"
            icon="pi pi-search"
            size="small"
            [loading]="loading()"
            styleClass="min-h-[44px]"
            (onClick)="buscar()"
          />
        </div>
      </div>
      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200':
              mensajeTipo() === 'warning',
            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200':
              mensajeTipo() === 'info',
            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200':
              mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }
      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }
      @if (!loading() && items().length > 0) {
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Centros de Trabajo</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400"
              >Registros del {{ registroInicio() }} hasta el
              {{ registroFin() }} de {{ totalRegistros() }}</span
            >
          }
        </div>
        <div class="md:hidden space-y-3 p-3">
          @for (item of items(); track item.nmconsecutivo) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Centro de Trabajo
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(formatearNombreCentro(item))"
                >
                  {{ textoTabla(formatearNombreCentro(item)) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Código:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.nmconsecutivo)
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionarItem(item)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="items()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '250px'"
            [selectionMode]="tableSelectionMode()"
            [(selection)]="seleccionado"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [dataKey]="
              (tableConfig().selection.dataKey || 'nmconsecutivo').toString()
            "
            [styleClass]="tableStyleClass()"
          >
            <ng-template pTemplate="header"
              ><tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Código">Código</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Centro de Trabajo"
                    >Centro de Trabajo</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr></ng-template
            >
            <ng-template pTemplate="body" let-item>
              <tr [pSelectableRow]="item">
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.nmconsecutivo)"
                    >{{ textoTabla(item.nmconsecutivo) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(formatearNombreCentro(item))"
                    >{{ textoTabla(formatearNombreCentro(item)) }}</span
                  >
                </td>
                <td>
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-text"
                    (click)="seleccionarItem(item)"
                  ></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <app-busqueda-paginador
          [currentPage]="paginaActual()"
          [totalPages]="totalPaginas()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="onSelectedPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="loadPreviousPage()"
          (next)="loadNextPage()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }
      @if (!loading() && busquedaRealizada && items().length === 0) {
        <div class="text-center text-gray-500 p-4 border rounded">
          No hay datos
        </div>
      }
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaCentroTrabajoDialogComponent
  extends BaseBusquedaPaginadaDialog<CentroTrabajo>
  implements OnInit
{
  private readonly beneficiarioRepository = inject(BeneficiarioRepository);
  private readonly sucursalRepository = inject(HttpSucursalRepository);
  titulo = input<string>('');

  nmconsecutivoBeneficiario = input<number | null>(null);
  nombreBeneficiario = input<string>('');
  cargarAlAbrir = input<boolean>(true);
  /** Origen de datos — determina qué tabla/fuente consulta el buscador. */
  origen = input<BuscadorCentroTrabajoOrigen>(BuscadorCentroTrabajoOrigen.BENEFICIARIO);
  /** NPOLIZA del cliente seleccionado — requerido cuando origen = SUCURSAL_SIN_OC. */
  npoliza = input<string | undefined>(undefined);
  /**
   * Solo SUCURSAL_SIN_OC en modo línea de atención: CDOFICINA de la oficina de la orden.
   * Legacy: CumplimientoSinOcViewImpl.crearParametrosAdicionales() líneas 552-557.
   */
  cdoficina = input<string | undefined>(undefined);

  onSelect = output<CentroTrabajoSeleccionado>();
  onClose = output<void>();

  visible = true;
  filtro: FiltroCentroTrabajo = {};
  seleccionado: CentroTrabajo | null = null;
  override busquedaRealizada = false;

  // Flags de navegación: los fija la RESPUESTA del backend (frontend-girop 17.7).
  // Sustituyen a los computed de la base paginada (que derivan de paginaActual).
  override hasNext = signal(false);
  override hasPrevious = signal(false);

  readonly tableConfig = computed(() =>
    busquedaCentroTrabajoPreset.createConfig({
      data: this.items() as unknown as Record<string, unknown>[],
      selection: {
        mode: 'single',
        dataKey: 'nmconsecutivo',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-busqueda-centro-trabajo',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron centros de trabajo'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '250px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de centros de trabajo',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = this.buildTableStyleClass(this.tableConfig);

  readonly tableSelectionMode = this.buildTableSelectionMode(this.tableConfig);

  tieneBeneficiario = computed(
    () =>
      this.origen() === BuscadorCentroTrabajoOrigen.SUCURSAL_SIN_OC
        ? !!this.npoliza()?.trim()
        : this.nmconsecutivoBeneficiario() !== null &&
          this.nmconsecutivoBeneficiario() !== undefined
  );

  ngOnInit(): void {
    if (this.cargarAlAbrir() && this.tieneBeneficiario()) {
      this.buscar();
    }
  }

  /**
   * Formatea el nombre del centro de trabajo.
   * El backend ya devuelve descripcionCompleta: CDCENTRO_TRABAJO||' - '||DSCENTRO_TRABAJO ||' - '||DSNOMBRE_COMERCIAL
   */
  formatearNombreCentro(item: CentroTrabajo): string {
    // Usar descripcionCompleta del backend si está disponible
    if (item.descripcionCompleta) {
      return item.descripcionCompleta;
    }
    // Fallback: construir manualmente
    const partes: string[] = [];
    if (item.cdcentroTrabajo) partes.push(item.cdcentroTrabajo);
    if (item.dscentroTrabajo) partes.push(item.dscentroTrabajo);
    if (item.nombreComercialBeneficiario)
      partes.push(item.nombreComercialBeneficiario);
    return partes.join(' - ');
  }

  /**
   * Ejecuta la búsqueda con los filtros actuales.
   * Ahora el componente hace la búsqueda internamente.
   * Legacy: UtilidadesBuscadores.buscarCentroTrabajo() - beneficiario es OPCIONAL
   */
  async buscar(): Promise<void> {
    this.sanitizarCampo('codigo', true);
    this.sanitizarCampo('nombre', true);
    this.mensaje.set('');
    this.busquedaRealizada = true;
    await this.ejecutarBusqueda(0);
  }

  /**
   * Sanitización XSS de texto libre (seguridad-girop §3). Mientras se teclea
   * conserva los espacios; antes de buscar también los recorta.
   */
  sanitizarCampo(campo: 'codigo' | 'nombre', recortar = false): void {
    const valor = this.filtro[campo];
    if (valor === undefined) return;
    this.filtro[campo] = recortar
      ? sanitizeText(valor)
      : sanitizeTextPreserveSpaces(valor);
  }

  /** Cursor devuelto por el backend para pedir la página siguiente (origen BENEFICIARIO/SUCURSAL_SIN_OC). */
  private nextCursorActual = signal<string | null>(null);

  /** Cursor devuelto por el backend para pedir la página anterior (origen BENEFICIARIO/SUCURSAL_SIN_OC). */
  private previousCursorActual = signal<string | null>(null);

  /**
   * Ejecuta la búsqueda en el backend usando paginación keyset/cursor (skill
   * acceso-datos-oracle §7 — nunca OFFSET), tanto para SUCURSAL_SIN_OC
   * (T094_SUCURSALES por NPOLIZA) como para BENEFICIARIO.
   * Si no hay beneficiario seleccionado, usa 0 para traer todos los centros de trabajo.
   */
  protected async ejecutarBusqueda(_start: number): Promise<void> {
    await this.cargarPagina(null, 'NEXT', 1);
  }

  /** Despacha la carga según el origen; la página solo cambia tras la respuesta exitosa. */
  private async cargarPagina(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina: number
  ): Promise<void> {
    if (this.origen() === BuscadorCentroTrabajoOrigen.SUCURSAL_SIN_OC) {
      await this.buscarPaginaSucursal(cursor, direction, pagina);
      return;
    }
    await this.buscarPaginaBeneficiario(cursor, direction, pagina);
  }

  /** Aplica cursores, flags y número de página de una respuesta exitosa. */
  private aplicarPaginacion(
    response:
      | {
          totalElements?: number;
          nextCursor?: string | null;
          previousCursor?: string | null;
          hasNext?: boolean;
          hasPrevious?: boolean;
        }
      | null
      | undefined,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina: number
  ): void {
    const total = response?.totalElements ?? 0;
    this.totalRegistros.set(total);
    this.nextCursorActual.set(response?.nextCursor ?? null);
    this.previousCursorActual.set(response?.previousCursor ?? null);
    this.hasNext.set(response?.hasNext ?? false);
    this.hasPrevious.set(response?.hasPrevious ?? false);
    if (direction === 'LAST') {
      const size = this.pageSize();
      this.paginaActual.set(
        size > 0 && total > 0 ? Math.max(1, Math.ceil(total / size)) : 1
      );
    } else {
      this.paginaActual.set(pagina);
    }
  }

  /** Limpia resultados ante un error; paginaActual no se modifica. */
  private limpiarPorError(): void {
    this.items.set([]);
    this.totalRegistros.set(0);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual.set(null);
    this.previousCursorActual.set(null);
  }

  /**
   * Ejecuta la búsqueda por cursor contra el backend de Beneficiario.
   * Una sola llamada devuelve content + totalElements.
   */
  private async buscarPaginaBeneficiario(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    const nmconsecutivoBenef = this.nmconsecutivoBeneficiario() ?? 0;
    const filtroNombre = this.filtro.nombre?.trim() || undefined;
    const filtroCodigo = this.filtro.codigo?.trim() || undefined;
    const size = this.pageSize();

    this.loading.set(true);
    this.mensaje.set('');

    try {
      const response =
        await this.beneficiarioRepository.buscarCentrosTrabajoPaginado(
          nmconsecutivoBenef,
          filtroNombre,
          filtroCodigo,
          cursor,
          size,
          direction
        );

      const content = response?.content ?? [];
      this.items.set(content);
      // paginaActual solo alimenta las etiquetas y cambia únicamente tras la respuesta.
      this.aplicarPaginacion(response, direction, pagina);

      if (content.length === 0) {
        this.mensaje.set('No se encontraron centros de trabajo');
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      console.error('Error buscando centros de trabajo:', error);
      this.mensaje.set(
        this.resolverMensajeErrorBusqueda(
          error,
          'Error al buscar centros de trabajo'
        )
      );
      this.mensajeTipo.set('error');
      this.limpiarPorError();
    } finally {
      this.loading.set(false);
    }
  }

  override async irPrimeraPagina(): Promise<void> {
    if (this.loading()) return;
    await this.cargarPagina(null, 'NEXT', 1);
  }

  override async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) return;
    await this.cargarPagina(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  override async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) return;
    await this.cargarPagina(
      this.nextCursorActual(),
      'NEXT',
      this.paginaActual() + 1
    );
  }

  override async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalRegistros() === 0) return;
    await this.cargarPagina(
      null,
      'LAST',
      Math.max(1, Math.ceil(this.totalRegistros() / this.pageSize()))
    );
  }

  /**
   * Búsqueda en T094_SUCURSALES para Sin Orientación de Servicio, con
   * paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
   * Mapea Sucursal al shape de CentroTrabajo para reutilizar la tabla existente:
   *   cdcentroTrabajo = CDSUCURSAL, dscentroTrabajo = DSSUCURSAL
   */
  private async buscarPaginaSucursal(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    const npolizaVal = this.npoliza();
    if (!npolizaVal?.trim()) {
      this.mensaje.set('Seleccione primero un cliente para buscar su centro de trabajo.');
      this.mensajeTipo.set('warning');
      return;
    }

    this.loading.set(true);
    this.mensaje.set('');

    try {
      const size = this.pageSize();
      const response = await this.sucursalRepository.buscar(
        npolizaVal,
        this.filtro.codigo?.trim() || undefined,
        this.filtro.nombre?.trim() || undefined,
        cursor,
        size,
        direction,
        this.cdoficina()?.trim() || undefined
      );

      const content = (response?.content ?? []).map((s, index) => ({
        nmconsecutivo: index + 1,          // único por página — evita selección masiva en PrimeNG
        cdcentroTrabajo: s.cdsucursal ?? '',
        dscentroTrabajo: s.dssucursal ?? '',
        descripcionCompleta: `${s.cdsucursal} - ${s.dssucursal}`,
        snactivo: 'S'
      } as CentroTrabajo));

      this.items.set(content);
      // paginaActual solo alimenta las etiquetas y cambia únicamente tras la respuesta.
      this.aplicarPaginacion(response, direction, pagina);

      if (content.length === 0) {
        this.mensaje.set('No se encontraron sucursales para esta póliza.');
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      console.error('Error buscando sucursales:', error);
      this.mensaje.set('Error al buscar sucursales');
      this.mensajeTipo.set('error');
      this.limpiarPorError();
    } finally {
      this.loading.set(false);
    }
  }

  limpiarFiltros(): void {
    this.filtro = {};
    this.items.set([]);
    this.clearMensajes();
    this.resetPaginacion();
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual.set(null);
    this.previousCursorActual.set(null);
    if (this.tieneBeneficiario()) this.buscar();
  }

  /**
   * Establece los items de la búsqueda (llamado desde el padre).
   */
  setItems(items: CentroTrabajo[], total?: number, pagina?: number): void {
    this.items.set(items);
    this.loading.set(false);
    this.totalRegistros.set(total !== undefined ? total : items.length);
    if (pagina) {
      this.paginaActual.set(pagina);
    }
    // hasNext/hasPrevious los fija la respuesta del backend; aquí no se infieren.
  }

  setLoading(loading: boolean): void {
    this.loading.set(loading);
  }

  setMensaje(
    mensaje: string,
    tipo: 'info' | 'warning' | 'error' = 'info'
  ): void {
    this.mensaje.set(mensaje);
    this.mensajeTipo.set(tipo);
    this.loading.set(false);
  }

  seleccionarItem(item: CentroTrabajo): void {
    const resultado: CentroTrabajoSeleccionado = {
      nmconsecutivo: item.nmconsecutivo,
      cdcentroTrabajo: item.cdcentroTrabajo,
      dscentroTrabajo: item.dscentroTrabajo,
      descripcionCompleta: this.formatearNombreCentro(item)
    };
    this.onSelect.emit(resultado);
    this.visible = false;
  }

  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
  }

}
